import React from 'react';
import Pressable from "react-native-web/dist/exports/Pressable";
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import { useThemeTokensCallback, verticalAlignRow } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
function selectContainerStyles(_ref) {
  let {
    backgroundColor,
    borderColor,
    borderWidth,
    borderStyle = 'solid',
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    justifyContent,
    verticalAlign,
    iconPosition,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderTopLeftRadius,
    borderBottomLeftRadius
  } = _ref;
  return {
    boxSizing: 'border-box',
    backgroundColor,
    borderColor,
    borderWidth,
    borderStyle,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    justifyContent,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderTopLeftRadius,
    borderBottomLeftRadius,
    ...verticalAlignRow(verticalAlign, iconPosition === 'right'),
    // Updated default alignment due to the change in Pressable's behavior from div to button on web.
    textAlign: Platform.OS === 'web' ? 'inherit' : undefined
  };
}

// TODO: use stack / spacer when available
function selectIconContainerStyles(_ref2) {
  let {
    iconGap,
    iconPaddingTop,
    iconPosition
  } = _ref2;
  const paddingSide = iconPosition === 'right' ? 'paddingLeft' : 'paddingRight';
  return {
    [paddingSide]: iconGap,
    paddingTop: iconPaddingTop
  };
}
function selectTextContainerStyles(_ref3) {
  let {
    textLine
  } = _ref3;
  return {
    textDecorationLine: textLine,
    flex: 1
  };
}
function selectIconTokens(tokens) {
  return {
    color: tokens.iconColor,
    size: tokens.iconSize
  };
}
const ExpandCollapseControl = /*#__PURE__*/React.forwardRef((_ref4, ref) => {
  let {
    onPress,
    isExpanded,
    children,
    tokens,
    accessibilityRole = 'button',
    variant,
    ...rest
  } = _ref4;
  const getTokens = useThemeTokensCallback('ExpandCollapseControl', tokens, variant);
  const selectedProps = selectProps({
    accessibilityRole,
    ...rest,
    accessibilityState: {
      ...(rest.accessibilityState || {}),
      expanded: isExpanded
    }
  });
  const getControlState = _ref5 => {
    let {
      pressed,
      hovered,
      focused
    } = _ref5;
    return {
      pressed,
      hover: hovered,
      focus: focused,
      expanded: isExpanded
    };
  };
  const getControlTokens = pressableState => getTokens(getControlState(pressableState));
  const getPressableStyle = pressableState => ({
    ...selectContainerStyles(getControlTokens(pressableState)),
    ...(Platform.OS === 'web' ? {
      outline: 'none'
    } : {})
  });
  return /*#__PURE__*/_jsx(Pressable, {
    ref: ref,
    ...selectedProps,
    onPress: onPress,
    style: getPressableStyle,
    children: pressableState => {
      const {
        icon: IconComponent,
        ...themeTokens
      } = getControlTokens(pressableState);
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [IconComponent && /*#__PURE__*/_jsx(View, {
          style: [selectIconContainerStyles(themeTokens), staticStyles.bubblePointerEvents],
          children: /*#__PURE__*/_jsx(IconComponent, {
            ...selectIconTokens(themeTokens)
          })
        }), /*#__PURE__*/_jsx(View, {
          style: [selectTextContainerStyles(themeTokens), staticStyles.bubblePointerEvents],
          children: typeof children === 'function' ? children(getControlState(pressableState)) : children
        })]
      });
    }
  });
});
ExpandCollapseControl.displayName = 'ExpandCollapseControl';
const staticStyles = StyleSheet.create({
  bubblePointerEvents: {
    pointerEvents: 'none'
  }
});
ExpandCollapseControl.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('ExpandCollapseControl'),
  variant: variantProp.propType,
  /**
   * ExpandCollapseControl's children are the content within the button that opens or closes this panel. May
   * accept a render function that recieves the control state: { pressed, hover, focus, expanded }
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Whether the linked ExpandCollapsePanel is opened or closed. Allows themes to set `expanded` styles.
   */
  isExpanded: PropTypes.bool,
  /**
   * Function called when the ExpandCollapse is pressed.
   */
  onPress: PropTypes.func.isRequired
};
export default ExpandCollapseControl;